import type {
  CarbonTargetDto,
  CarbonTargetFilterScope,
  CarbonTargetRow,
  ScenarioCarbonBlock,
  ScenarioUsePhaseByCohort,
  ScenarioUsePhaseProjection,
  SensitivityAttributeResult,
} from '@siliconx/shared';
import { resolveCarbonTarget } from '@siliconx/shared';
import { SectionLabel } from '@siliconx/shared/ui/SectionLabel';
import { CalculationBasisTrigger } from './CalculationBasisDrawer';
import { AbatementRankingTable, buildAbatementRow } from './carbon/AbatementRankingTable';
import { categoryPointTotal, kgToTonnes } from './carbon/carbonCategories';
import { carbonOrgTargetLabel } from './carbon/carbonCopy';
import {
  CarbonCategoryChips,
  CarbonForecastFootnotes,
  CarbonMethodBadge,
  MethodologySheetTrigger,
} from './carbon/CarbonDisclosures';
import { CarbonForecastChart } from './carbon/CarbonForecastChart';

/**
 * SIL-349 (T11, resources/plans/use-phase-carbon.md, "Financial scenario"
 * surface row: "rate prorated over horizon_months ... forecast, not
 * inventory") — the scenario-compare + sensitivity-tornado rendering of
 * `useCarbon` (SIL-346, T8).
 *
 * Kept as its own file (not inlined in the wizard route) for two reasons:
 * 1. Testability — the wizard route wires TanStack Router + Query; these
 *    are pure presentational components over already-fetched data, so they
 *    can be unit-tested directly with fixture props.
 * 2. The forecast/inventory visual distinction (see `ScenarioCarbonForecast`
 *    below) is a single, reviewable place rather than styling scattered
 *    across the route file.
 */

export function formatKgco2e(n: number): string {
  return `${Math.round(n).toLocaleString('en-US')} kg CO2e`;
}

// ---------------------------------------------------------------------------
// Scenario-wide + per-cohort forecast card
// ---------------------------------------------------------------------------

/**
 * Visually distinct from the Scope 2 inventory figure on asset detail
 * (SIL-347): that surface is a solid card (see `CarbonClaim.tsx`'s
 * `border-green-200 bg-green-50`, the SAME solid-card treatment SIL-347
 * uses for the reported Scope 2 rate) reporting a MEASURED-OR-DEFAULTED
 * rate as of today. This is a hatched/dashed violet card carrying an
 * explicit "FORECAST" badge and prose calling out the difference — a
 * different color family (violet, used nowhere else for carbon), a dashed
 * border (nowhere else in this app means "provisional"), and a badge that
 * says the word "forecast" so a screenshot or a printed page still carries
 * the distinction without color.
 */
/**
 * ── SIL-440 (W4, spec §6 "Variant A" row 1) ──────────────────────────────
 * This same card is now the full-width FORECAST surface the sustainability
 * spec locks: the SIL-436 category-tagged `carbon` block renders above the
 * original SIL-346 use-phase section as a category-stacked monthly area
 * chart with the baseline overlay, the target line for the active filter
 * scope, the §2 method badge, §4 category chips, the §14 methodology-sheet
 * trigger, the $/tCO2e abatement ranking, and the §1/§3/§5 footnotes.
 *
 * Every new prop is OPTIONAL and additive: a caller that passes only
 * `useCarbon` renders exactly the pre-SIL-440 card. That is deliberate — the
 * two blocks answer different questions (SIL-346 is a single device-rate x
 * PUE total per cohort; SIL-436 is the category-tagged
 * baseline/scenario/delta series) and neither is a replacement for the
 * other, so neither is allowed to make the card fail to render.
 *
 * The advisory numbers are NOT on this card and never will be — they live in
 * `carbon/CarbonAdvisoryModule.tsx`, a separate quarantined module fed by a
 * separate response field (spec §5: "never summed, netted, or shared an axis
 * with the footprint").
 */
export function ScenarioCarbonForecast({
  useCarbon,
  horizonMonths,
  carbon,
  carbonTargets,
  targetScope = { scopeType: 'org' },
  deltaNetPositionUsd,
  scenarioId,
}: {
  useCarbon: ScenarioUsePhaseProjection | null | undefined;
  horizonMonths: number;
  /** SIL-436 category-tagged series. Absent for pre-SIL-440 callers, which
   * then keep the original card. */
  carbon?: ScenarioCarbonBlock | null;
  /** Every `carbon_target` row the caller can see (SIL-437
   * `GET /sustainability/targets`). The chart draws the ONE row matching
   * `targetScope` — org and team lenses are independent and never summed
   * (spec §4). */
  carbonTargets?: readonly CarbonTargetDto[];
  /** The active filter scope. The scenario results surface has no team
   * filter in v1, so this defaults to the org headline lens. */
  targetScope?: CarbonTargetFilterScope;
  /** Scenario-minus-baseline NET POSITION delta, for the $/tCO2e ranking.
   * Undefined suppresses the abatement table rather than pricing abatement
   * against an invented $0. */
  deltaNetPositionUsd?: number;
  /** Enables the methodology-sheet download (the sheet is generated per
   * scenario). Null for an unsaved draft. */
  scenarioId?: string | null;
}) {
  // "never drop assets" applies to a compute that resolves zero snapshots
  // too (plan §"Financial scenario" surface row) — the API always attaches
  // an all-zero block rather than omitting the field, so absence here means
  // genuinely "not computed yet" (pre-first-compare), not "zero carbon".
  if (!useCarbon && !carbon) return null;

  const target = carbon && carbonTargets ? resolveCarbonTarget(carbonTargets, targetScope) : null;

  return (
    <div className="rounded-xl border-2 border-dashed border-violet-300 bg-violet-50/60 p-5">
      {carbon && (
        <ForecastChartSection
          carbon={carbon}
          target={target}
          deltaNetPositionUsd={deltaNetPositionUsd}
          scenarioId={scenarioId ?? null}
        />
      )}

      {useCarbon && (
        <UsePhaseSection
          useCarbon={useCarbon}
          horizonMonths={horizonMonths}
          dividedFromChart={!!carbon}
        />
      )}

      {carbon && <CarbonForecastFootnotes />}
    </div>
  );
}

/**
 * Row 1 of the Variant-A layout — the category-stacked monthly chart, the
 * §4 category chips, and the $/tCO2e abatement ranking.
 */
function ForecastChartSection({
  carbon,
  target,
  deltaNetPositionUsd,
  scenarioId,
}: {
  carbon: ScenarioCarbonBlock;
  /** The kernel's narrow pure-input row (what `resolveCarbonTarget` returns),
   * not the wider wire DTO — this section reads only `tco2ePerYr`, and
   * narrowing here keeps the audit/derived fields out of a rendering path. */
  target: CarbonTargetRow | null;
  deltaNetPositionUsd: number | undefined;
  scenarioId: string | null;
}) {
  const finalPoint = carbon.series.length > 0 ? carbon.series[carbon.series.length - 1]! : null;
  const deltaTco2e = finalPoint ? kgToTonnes(categoryPointTotal(finalPoint.delta)) : 0;

  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center rounded bg-violet-600 px-1.5 py-0.5 text-xs font-bold uppercase tracking-wider text-white">
          Forecast
        </span>
        <SectionLabel className="inline-flex items-center gap-1.5 text-violet-900">
          Carbon footprint forecast
          <CalculationBasisTrigger title="Carbon footprint forecast" basis={carbon.basis} />
        </SectionLabel>
        <CarbonMethodBadge />
        {scenarioId && (
          <span className="ml-auto">
            <MethodologySheetTrigger scenarioId={scenarioId} />
          </span>
        )}
      </div>

      <div className="mt-3">
        <CarbonForecastChart
          series={carbon.series}
          // SIL-474: a reduction_percent target has no flat tco2ePerYr
          // number (null by DB CHECK) — hide the absolute-budget target
          // line entirely rather than render a false "0 tCO2e/yr" line.
          // The FY target card (carbon/FyReductionTargetCard.tsx) is the
          // reduction_percent equivalent surface, mounted separately.
          targetTco2ePerYr={target?.tco2ePerYr ?? null}
          targetLabel={target?.tco2ePerYr != null ? carbonOrgTargetLabel(target.tco2ePerYr) : null}
        />
      </div>

      <div className="mt-2">
        <CarbonCategoryChips />
      </div>

      {deltaNetPositionUsd !== undefined && finalPoint && (
        <div className="mt-4 rounded-lg bg-white/70 p-3">
          <h4 className="mb-1 text-xs uppercase tracking-widest text-violet-700">
            Abatement ranking (footprint deltas only)
          </h4>
          <AbatementRankingTable
            rows={[
              buildAbatementRow({
                action: 'All scenario actions combined',
                deltaTco2e,
                deltaNetPositionUsd,
              }),
            ]}
            gapNote={
              'Ranked at scenario grain, not per action: the compare response carries one ' +
              'footprint delta for the whole scenario, so a per-action $/tCO2e would have to ' +
              'be apportioned by an assumption this surface does not have. Per-cohort carbon ' +
              'deltas are the prerequisite for ranking individual actions.'
            }
          />
        </div>
      )}
    </>
  );
}

/**
 * The original SIL-346 use-phase block, unchanged in content — a single
 * device-rate x PUE total per cohort. Kept BELOW the category series rather
 * than replaced by it: the per-cohort accrual-stop and unmodeled-remainder
 * disclosures it carries have no equivalent in the SIL-436 block.
 */
function UsePhaseSection({
  useCarbon,
  horizonMonths,
  dividedFromChart,
}: {
  useCarbon: ScenarioUsePhaseProjection;
  horizonMonths: number;
  dividedFromChart: boolean;
}) {
  return (
    <>
      {dividedFromChart && <hr className="my-5 border-violet-200" />}
      <div className="flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center rounded bg-violet-600 px-1.5 py-0.5 text-xs font-bold uppercase tracking-wider text-white">
          Forecast
        </span>
        <SectionLabel className="inline-flex items-center gap-1.5 text-violet-900">
          Use-phase carbon (Scope 2, location-based)
          <CalculationBasisTrigger title="Use-phase carbon forecast" basis={useCarbon.basis} />
        </SectionLabel>
      </div>

      <p className="mt-1.5 max-w-2xl text-xs text-violet-800">
        A modeled projection over this scenario&apos;s {horizonMonths}-month horizon — not a
        reported inventory figure. It will not match, and should not be compared line-for-line
        against, the Scope 2 figure on an asset&apos;s detail page, which reports carbon to date
        from measured or archetype data, never a what-if.
      </p>

      <div className="mt-3 font-mono text-2xl font-semibold tabular-nums text-violet-900">
        {formatKgco2e(useCarbon.projectedKgco2e)}
      </div>

      {/* Default PUE disclosed inline with its source — never only in a
       * tooltip (plan §"PUE — applied, not footnoted"). */}
      <div className="mt-1 text-xs text-violet-700">
        PUE {useCarbon.pue.toFixed(2)} applied —{' '}
        {useCarbon.pueSource === 'manual_entry'
          ? "this scenario's own value"
          : 'published industry default (no PUE set on this scenario)'}
        .
      </div>

      {useCarbon.byCohort.length > 0 && (
        <div className="mt-4 overflow-hidden rounded-md border border-violet-200 bg-white">
          <div
            className="grid bg-violet-50 px-3 py-1.5 text-xs uppercase tracking-widest text-violet-700"
            style={{ gridTemplateColumns: 'minmax(0,1.3fr) repeat(2, minmax(0,0.9fr)) minmax(0,1.7fr)' }}
          >
            <span>Cohort</span>
            <span className="text-right">Device rate /yr</span>
            <span className="text-right">Projected</span>
            <span>Accrual</span>
          </div>
          {useCarbon.byCohort.map((cohort) => (
            <CohortCarbonRow key={cohort.cohortId} cohort={cohort} horizonMonths={horizonMonths} />
          ))}
        </div>
      )}
    </>
  );
}

function CohortCarbonRow({
  cohort,
  horizonMonths,
}: {
  cohort: ScenarioUsePhaseByCohort;
  horizonMonths: number;
}) {
  // An action timed at/beyond the horizon end never happens inside the run
  // (resolveCarbonAccrualWindow's own note) — carbonAccrualStopMonth then
  // equals horizonMonths, and reads identically to a hold, which is correct.
  const stopsEarly = cohort.carbonAccrualStopMonth < horizonMonths;

  return (
    <div
      className="grid border-t border-violet-100 px-3 py-2 text-xs text-zinc-800"
      style={{ gridTemplateColumns: 'minmax(0,1.3fr) repeat(2, minmax(0,0.9fr)) minmax(0,1.7fr)' }}
    >
      <span className="font-sans text-zinc-700">{cohort.label}</span>
      <span className="text-right font-mono tabular-nums">
        {Math.round(cohort.deviceAnnualRateKgco2eqPerYr).toLocaleString('en-US')}
      </span>
      <span className="text-right font-mono tabular-nums">{formatKgco2e(cohort.projectedKgco2e)}</span>
      <span className="font-sans leading-snug">
        {stopsEarly ? (
          <span className="font-medium text-amber-700">
            Stops accruing at month {cohort.carbonAccrualStopMonth} of {horizonMonths}
          </span>
        ) : (
          <span className="text-zinc-500">Full {horizonMonths}-month horizon</span>
        )}
        {/* The explicit gap — rendered as prose, never as a "0" or "—" cell
         * (plan: "carbonPostActionUnmodeled must render as an explicit gap,
         * never as zero or a dash"). The replacement SKU owns no assets yet,
         * so there is no honest rate to project past this month. */}
        {cohort.carbonPostActionUnmodeled && (
          <span className="mt-0.5 block italic text-amber-700" data-testid="post-action-unmodeled-gap">
            Not modeled after month {cohort.carbonAccrualStopMonth} — the replacement SKU has no
            owned assets yet, so there is no rate to project for the remainder.
          </span>
        )}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sensitivity tornado — carbon column beside the existing dollar bar.
// `pue` is the one attribute that moves this figure at all (every other
// attribute perturbs a $ input the carbon math never reads); a 0 swing on a
// non-`pue` row is a true, disclosed zero, not a missing measurement.
// ---------------------------------------------------------------------------

export function SensitivityCarbonSwing({ attr }: { attr: SensitivityAttributeResult }) {
  if (!attr.applicable || attr.swingKgco2e === null) {
    return (
      <span className="font-mono text-[13px] tabular-nums text-zinc-400">
        {formatKgco2e(attr.baseCarbonKgco2e)} (base — no swing tested)
      </span>
    );
  }

  const sign = attr.swingKgco2e > 0 ? '+' : attr.swingKgco2e < 0 ? '−' : '±';
  return (
    <span
      className={`font-mono text-xs tabular-nums ${
        attr.swingKgco2e === 0 ? 'text-zinc-400' : 'text-violet-700'
      }`}
    >
      {sign}
      {formatKgco2e(Math.abs(attr.swingKgco2e))}
    </span>
  );
}
